/* eslint-disable react-refresh/only-export-components */
import { uuid } from '../lib/uuid';
import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'wm-inventory-v1';

function loadFromStorage() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function saveToStorage(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch { /* quota exceeded — silently ignore */ }
}

// ─── Seed data ────────────────────────────────────────────────────────────────
const SEED_INVENTORY = [
  {
    id: 'inv-001',
    sku: '3M-1080-MB',
    name: '3M 1080 Series Matte Black',
    brand: '3M',
    color: 'Matte Black',
    unitType: 'roll',
    qty: 3,
    reorderAt: 1,
    costPerUnit: 180,
    notes: '',
    locationId: 'loc-001',
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-01T00:00:00Z',
  },
  {
    id: 'inv-002',
    sku: '3M-1080-GW',
    name: '3M 1080 Series Gloss White',
    brand: '3M',
    color: 'Gloss White',
    unitType: 'roll',
    qty: 5,
    reorderAt: 2,
    costPerUnit: 180,
    notes: '',
    locationId: 'loc-001',
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-01T00:00:00Z',
  },
  {
    id: 'inv-003',
    sku: 'AVY-SW900-SB',
    name: 'Avery Dennison SW900 Satin Black',
    brand: 'Avery Dennison',
    color: 'Satin Black',
    unitType: 'roll',
    qty: 2,
    reorderAt: 1,
    costPerUnit: 165,
    notes: '',
    locationId: 'loc-001',
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-01T00:00:00Z',
  },
  {
    id: 'inv-004',
    sku: 'ORA-970-GC',
    name: 'ORACAL 970RA Gloss Chrome',
    brand: 'ORACAL',
    color: 'Gloss Chrome',
    unitType: 'roll',
    qty: 1,
    reorderAt: 1,
    costPerUnit: 210,
    notes: 'LOW STOCK — reorder immediately',
    locationId: 'loc-001',
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-01T00:00:00Z',
  },
  {
    id: 'inv-005',
    sku: 'XPL-ULT-PPF',
    name: 'XPEL Ultimate Plus PPF',
    brand: 'XPEL',
    color: 'Clear',
    unitType: 'sqft',
    qty: 250,
    reorderAt: 50,
    costPerUnit: 8,
    notes: '',
    locationId: 'loc-001',
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-01T00:00:00Z',
  },
  {
    id: 'inv-006',
    sku: '3M-CERAMIC-KIT',
    name: '3M Ceramic Coating Kit',
    brand: '3M',
    color: 'N/A',
    unitType: 'kit',
    qty: 4,
    reorderAt: 2,
    costPerUnit: 120,
    notes: '',
    locationId: 'loc-001',
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-01T00:00:00Z',
  },
  {
    id: 'inv-007',
    sku: 'SQGE-TOOLS-SET',
    name: 'Squeegee Tools Set',
    brand: 'Generic',
    color: 'N/A',
    unitType: 'each',
    qty: 12,
    reorderAt: 5,
    costPerUnit: 15,
    notes: '',
    locationId: 'loc-001',
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-01T00:00:00Z',
  },
  {
    id: 'inv-008',
    sku: 'IPA-PREP-GAL',
    name: 'IPA Prep Spray',
    brand: 'Generic',
    color: 'N/A',
    unitType: 'gallon',
    qty: 6,
    reorderAt: 3,
    costPerUnit: 22,
    notes: '',
    locationId: 'loc-001',
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-01T00:00:00Z',
  },
];

// ─── Context ─────────────────────────────────────────────────────────────────
const InventoryContext = createContext(null);

// ─── Provider ────────────────────────────────────────────────────────────────
export function InventoryProvider({ children }) {
  const [inventory, setInventory] = useState(() => {
    if (import.meta.env.VITE_DEV_AUTH === '1') return SEED_INVENTORY;
    return loadFromStorage();
  });

  // Write-through: persist to localStorage on every state change
  useEffect(() => { saveToStorage(inventory); }, [inventory]);

  // ─── Derived ───────────────────────────────────────────────────────────────
  const lowStockItems = inventory.filter(item => item.qty <= item.reorderAt);
  const lowStockCount = lowStockItems.length;

  // ─── CRUD ──────────────────────────────────────────────────────────────────
  const addItem = useCallback((data) => {
    const now = new Date().toISOString();
    const newItem = {
      id: uuid(),
      sku: '',
      name: '',
      brand: '',
      color: '',
      unitType: 'roll',
      qty: 0,
      reorderAt: 1,
      costPerUnit: 0,
      notes: '',
      locationId: 'loc-001',
      ...data,
      createdAt: now,
      updatedAt: now,
    };
    setInventory(prev => [...prev, newItem]);
    return newItem;
  }, []);

  const updateItem = useCallback((id, patch) => {
    setInventory(prev => prev.map(item =>
      item.id === id
        ? { ...item, ...patch, updatedAt: new Date().toISOString() }
        : item
    ));
  }, []);

  const deleteItem = useCallback((id) => {
    setInventory(prev => prev.filter(item => item.id !== id));
  }, []);

  const getItemById = useCallback((id) => {
    return inventory.find(item => item.id === id) || null;
  }, [inventory]);

  /**
   * Adjust the quantity of an item by delta (positive = add, negative = use).
   * Appends a timestamped note to item.notes.
   */
  const adjustQty = useCallback((id, delta, note = '') => {
    const now = new Date().toISOString();
    setInventory(prev => prev.map(item => {
      if (item.id !== id) return item;
      const newQty = Math.max(0, item.qty + delta);
      const logLine = `[${now.slice(0, 10)}] ${delta > 0 ? '+' : ''}${delta} — ${note || 'Manual adjustment'}`;
      const existingNotes = item.notes ? item.notes.trim() : '';
      const updatedNotes = existingNotes ? `${existingNotes}\n${logLine}` : logLine;
      return { ...item, qty: newQty, notes: updatedNotes, updatedAt: now };
    }));
  }, []);

  return (
    <InventoryContext.Provider value={{
      inventory,
      lowStockItems,
      lowStockCount,
      addItem,
      updateItem,
      deleteItem,
      getItemById,
      adjustQty,
    }}>
      {children}
    </InventoryContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────
export function useInventory() {
  const ctx = useContext(InventoryContext);
  if (!ctx) throw new Error('useInventory must be used within InventoryProvider');
  return ctx;
}
